import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  getMetaAppId,
  getMetaConfigId,
  getMetaGraphVersion,
} from "../../config";

const FB_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const FB_SDK_ID = "facebook-jssdk";

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

let sdkReadyPromise = null;

/**
 * Carrega o Facebook JS SDK no padrão oficial da Meta (fbAsyncInit + insertBefore).
 * https://developers.facebook.com/docs/javascript/quickstart
 */
const loadFacebookSdk = (appId, version) => {
  if (sdkReadyPromise) {
    return sdkReadyPromise.then(FB => {
      // Re-init se app/versão mudarem
      FB.init({
        appId: String(appId),
        cookie: true,
        xfbml: true,
        version,
      });
      return FB;
    });
  }

  sdkReadyPromise = new Promise((resolve, reject) => {
    const finish = () => {
      try {
        if (!document.getElementById("fb-root")) {
          const root = document.createElement("div");
          root.id = "fb-root";
          document.body.insertBefore(root, document.body.firstChild);
        }

        window.FB.init({
          appId: String(appId),
          cookie: true,
          xfbml: true,
          version,
        });

        if (window.FB.AppEvents && window.FB.AppEvents.logPageView) {
          window.FB.AppEvents.logPageView();
        }

        // Padrão Meta: descobrir status de login após o init
        window.FB.getLoginStatus(function onLoginStatus() {
          resolve(window.FB);
        });
      } catch (err) {
        sdkReadyPromise = null;
        reject(err);
      }
    };

    window.fbAsyncInit = finish;

    if (window.FB) {
      finish();
      return;
    }

    (function injectSdk(d, s, id) {
      const existing = d.getElementById(id);
      if (existing) {
        const started = Date.now();
        const wait = setInterval(() => {
          if (window.FB) {
            clearInterval(wait);
            finish();
          } else if (Date.now() - started > 15000) {
            clearInterval(wait);
            sdkReadyPromise = null;
            reject(new Error("Timeout ao carregar o Facebook SDK"));
          }
        }, 50);
        return;
      }

      const js = d.createElement(s);
      js.id = id;
      js.src = FB_SDK_SRC;
      js.async = true;
      js.onerror = () => {
        sdkReadyPromise = null;
        reject(
          new Error(
            "Falha ao carregar o Facebook SDK. Verifique bloqueadores e Allowed Domains for JS SDK."
          )
        );
      };
      const fjs = d.getElementsByTagName(s)[0];
      if (fjs && fjs.parentNode) {
        fjs.parentNode.insertBefore(js, fjs);
      } else {
        d.head.appendChild(js);
      }
    })(document, "script", FB_SDK_ID);
  });

  return sdkReadyPromise;
};

const parseSessionPayload = raw => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
};

const describeFbFailure = response => {
  if (!response) {
    return "Facebook não retornou resposta. Verifique domínio JS SDK e Configuration ID na Meta.";
  }
  if (response.status === "unknown") {
    return (
      "Facebook retornou status unknown (popup bloqueado, usuário não logado no Facebook, ou domínio não autorizado). " +
      "Confirme App Domains + Allowed Domains for JS SDK = oficial.simpatxia.online e " +
      "Valid OAuth Redirect URI = https://oficial.simpatxia.online/waba-signup. Permita popups."
    );
  }
  if (response.status === "not_authorized") {
    return "App Facebook não autorizado para este usuário. Adicione-se como Admin/Developer/Tester do app.";
  }
  return "Cadastro cancelado ou sem code. Confira Tech Provider + Config ID + domínios HTTPS.";
};

const exchangeEmbeddedCode = async ({
  code,
  session,
  onSuccess,
}) => {
  const { data } = await api.post("/whatsapp/meta/embedded-signup", {
    code,
    wabaId: session.wabaId,
    phoneNumberId: session.phoneNumberId,
    businessId: session.businessId,
    coexistence: session.coexistence !== false,
  });

  toast.success(
    "WhatsApp conectado via Meta. Abra o app WhatsApp Business se for coexistência."
  );
  if (onSuccess) onSuccess(data);
  return data;
};

const waitForSessionInfo = async sessionInfoRef => {
  let session = sessionInfoRef.current;
  for (let i = 0; i < 30 && !session?.phoneNumberId; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(100);
    session = sessionInfoRef.current;
  }
  return session;
};

/**
 * Hook do Facebook Embedded Signup (coexistência / WhatsApp Business App).
 */
const useMetaEmbeddedSignup = ({ onSuccess } = {}) => {
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [metaConfig, setMetaConfig] = useState(null);
  const [error, setError] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const sessionInfoRef = useRef(null);

  const refreshConfig = useCallback(async ({ requireAuth = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/whatsapp/meta/config");
      const merged = {
        ...data,
        appId: data.appId || getMetaAppId(),
        configId: data.configId || getMetaConfigId(),
        graphVersion: data.graphVersion || getMetaGraphVersion(),
      };
      setMetaConfig(merged);
      return merged;
    } catch (err) {
      const status = err?.response?.status;
      if (requireAuth && (status === 401 || status === 403)) {
        throw new Error(
          "Sessão expirada. Saia e entre novamente no Whaticket antes de conectar o WhatsApp Meta."
        );
      }

      const fallback = {
        enabled: Boolean(getMetaAppId() && getMetaConfigId()),
        isMetaCloud: false,
        appId: getMetaAppId(),
        configId: getMetaConfigId(),
        graphVersion: getMetaGraphVersion(),
      };
      setMetaConfig(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshConfig();
  }, [refreshConfig]);

  // Pré-carrega SDK no padrão Meta assim que tivermos appId
  useEffect(() => {
    const appId = metaConfig?.appId || getMetaAppId();
    const graphVersion = metaConfig?.graphVersion || getMetaGraphVersion();
    if (!appId) return undefined;

    let cancelled = false;
    loadFacebookSdk(appId, graphVersion)
      .then(() => {
        if (!cancelled) setSdkReady(true);
      })
      .catch(() => {
        if (!cancelled) setSdkReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, [metaConfig?.appId, metaConfig?.graphVersion]);

  useEffect(() => {
    const sessionInfoListener = event => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com" &&
        event.origin !== "https://business.facebook.com"
      ) {
        return;
      }

      const payload = parseSessionPayload(event.data);
      if (!payload || payload.type !== "WA_EMBEDDED_SIGNUP") {
        return;
      }

      const eventName = payload.event;
      if (
        eventName === "FINISH" ||
        eventName === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"
      ) {
        const data = payload.data || {};
        sessionInfoRef.current = {
          wabaId: data.waba_id || data.wabaId,
          phoneNumberId: data.phone_number_id || data.phoneNumberId,
          businessId: data.business_id || data.businessId,
          coexistence:
            eventName === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" ||
            Boolean(data.coexistence),
        };
      }

      if (
        eventName === "CANCEL" ||
        eventName === "error" ||
        eventName === "ERROR"
      ) {
        sessionInfoRef.current = {
          ...(sessionInfoRef.current || {}),
          cancelled: true,
          errorMessage:
            payload.data?.error_message ||
            payload.data?.message ||
            "Embedded Signup cancelado/erro na Meta",
        };
      }
    };

    window.addEventListener("message", sessionInfoListener);
    return () => window.removeEventListener("message", sessionInfoListener);
  }, []);

  const handleAuthResponse = useCallback(
    response =>
      Promise.resolve()
        .then(async () => {
          const code = response?.authResponse?.code;
          if (!code) {
            const sessionErr = sessionInfoRef.current?.errorMessage;
            throw new Error(sessionErr || describeFbFailure(response));
          }

          const session = await waitForSessionInfo(sessionInfoRef);
          if (!session?.phoneNumberId || !session?.wabaId) {
            throw new Error(
              "Não recebemos waba_id/phone_number_id do Embedded Signup (postMessage). Confirme sessionInfoVersion=3."
            );
          }

          return exchangeEmbeddedCode({ code, session, onSuccess });
        }),
    [onSuccess]
  );

  const startEmbeddedSignup = useCallback(async () => {
    setConnecting(true);
    setError(null);
    sessionInfoRef.current = null;

    try {
      const cfg = await refreshConfig({ requireAuth: true });
      const appId = cfg.appId || getMetaAppId();
      const configId = cfg.configId || getMetaConfigId();
      const graphVersion = cfg.graphVersion || getMetaGraphVersion();

      if (!appId || !configId) {
        throw new Error(
          "Meta App ID / Config ID não configurados (META_APP_ID / META_EMBEDDED_CONFIG_ID)."
        );
      }

      const FB = await loadFacebookSdk(appId, graphVersion);
      setSdkReady(true);

      await new Promise((resolve, reject) => {
        // Callback DEVE ser function (não async) — exigência do SDK.
        FB.login(
          function onFbLogin(response) {
            handleAuthResponse(response).then(resolve).catch(reject);
          },
          {
            config_id: configId,
            response_type: "code",
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: "whatsapp_business_app_onboarding",
              sessionInfoVersion: "3",
              version: "v4",
            },
          }
        );
      });
    } catch (err) {
      setError(err);
      toastError(err);
    } finally {
      setConnecting(false);
    }
  }, [handleAuthResponse, refreshConfig]);

  /**
   * Callback no padrão Meta (checkLoginState) — útil com fb:login-button.
   */
  const checkLoginState = useCallback(() => {
    if (!window.FB) {
      toastError(new Error("Facebook SDK ainda não carregou."));
      return;
    }

    setConnecting(true);
    window.FB.getLoginStatus(function onStatus(response) {
      handleAuthResponse(response)
        .catch(err => {
          setError(err);
          toastError(err);
        })
        .finally(() => setConnecting(false));
    });
  }, [handleAuthResponse]);

  return {
    loading,
    connecting,
    error,
    metaConfig,
    sdkReady,
    refreshConfig,
    startEmbeddedSignup,
    checkLoginState,
    isMetaCloud: Boolean(metaConfig?.isMetaCloud),
    enabled: Boolean(metaConfig?.enabled),
  };
};

export default useMetaEmbeddedSignup;
